import { ALL_DATA_TYPES } from '@repo/advanced-filters';
import {
  type ActivityAttribute as ActivityAttributeType,
  type ActivityAttributeOption,
  getActivityAttributeOptions,
  type GetActivityAttributeOptionsResponse,
  getActivityAttributes,
  getCustomActivities,
  type GetCustomActivitiesType,
  transformCustomActivities,
} from '@repo/crm-atoms';
import {
  useGetGraphqlDataQuery,
  useLazyGetGraphqlDataQuery,
  type UseQueryResult,
} from '@repo/services';
import { OwnerSelectDropdown } from '@repo/ui';
import { useDebounce } from '@repo/utils';
import {
  Col, Flex, Row, Select, Typography,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';

import { type ActivityUpdateCriteria } from './LeadActivityUpdateMenu';
import styles from './LeadActivityUpdateMenu.module.scss';

const DEBOUNCE_DELAY = 500;

interface GetActivityAttributesType {
  activityAttribute: ActivityAttributeType[];
}

interface ActivityUpdateSelectionProps {
  activityUpdateConfig: ActivityUpdateCriteria;
  onConfigChange: (config: ActivityUpdateCriteria) => void;
}

export default function ActivityUpdateSelection({
  activityUpdateConfig,
  onConfigChange,
}: ActivityUpdateSelectionProps) {
  const [searchText, setSearchText] = useState<string>('');
  const debouncedSearchText = useDebounce(searchText, DEBOUNCE_DELAY);

  const { data: activitiesData, isLoading: activitiesLoading } = useGetGraphqlDataQuery<
  UseQueryResult<GetCustomActivitiesType>
  >({
    document: getCustomActivities,
  });

  const shouldFetchAttributes = !!activityUpdateConfig.activityId;
  const {
    currentData: activityAttributesData,
    isLoading: attributesLoading,
  } = useGetGraphqlDataQuery<UseQueryResult<GetActivityAttributesType>>({
    document: getActivityAttributes,
    variables: { activityId: activityUpdateConfig.activityId },
  }, {
    skip: !shouldFetchAttributes,
  });

  const [fetchAttributeOptions, { data: attributeOptionsData }] = useLazyGetGraphqlDataQuery<
  UseQueryResult<GetActivityAttributeOptionsResponse>
  >();

  useEffect(() => {
    if (activitiesData?.activities?.length && !activityUpdateConfig.activityId) {
      const firstActivity = activitiesData.activities[0];
      if (firstActivity) {
        onConfigChange({
          ...activityUpdateConfig,
          activityId: parseInt(String(firstActivity.id), 10),
        });
      }
    }
  }, [activitiesData?.activities, activityUpdateConfig, onConfigChange]);

  useEffect(() => {
    const currentActivityId = activityUpdateConfig.activityId;
    const hasAttributes = activityAttributesData?.activityAttribute?.length;
    const needsDefaultAttribute = !activityUpdateConfig.activityAttributeId;

    const attributesMatchCurrentActivity = hasAttributes
      && activityAttributesData.activityAttribute.some(
        (attr) => parseInt(attr.activityId, 10) === currentActivityId,
      );

    if (shouldFetchAttributes
        && attributesMatchCurrentActivity
        && needsDefaultAttribute
        && !attributesLoading) {
      const firstAttribute = activityAttributesData.activityAttribute[0];
      if (firstAttribute) {
        const config: ActivityUpdateCriteria = {
          ...activityUpdateConfig,
          activityAttributeId: parseInt(firstAttribute.id, 10),
        };

        if (firstAttribute.dataType === ALL_DATA_TYPES.dropdown
            || firstAttribute.dataType === ALL_DATA_TYPES.multi_select) {
          config.fromValue = 'any';
          config.toValue = 'any';
        } else if (firstAttribute.dataType === ALL_DATA_TYPES.reference_user) {
          config.toValue = 'any';
        }

        onConfigChange(config);
      }
    }
  }, [
    shouldFetchAttributes,
    activityAttributesData?.activityAttribute,
    activityUpdateConfig,
    onConfigChange,
    attributesLoading,
    activityUpdateConfig.activityId,
  ]);

  useEffect(() => {
    const selectedAttribute = activityAttributesData?.activityAttribute?.find(
      (attr: ActivityAttributeType) => parseInt(attr.id, 10) === activityUpdateConfig.activityAttributeId,
    );

    const attributeBelongsToCurrentActivity = selectedAttribute
      && parseInt(selectedAttribute.activityId, 10) === activityUpdateConfig.activityId;

    if (attributeBelongsToCurrentActivity
        && activityUpdateConfig.activityAttributeId
        && (selectedAttribute.dataType === ALL_DATA_TYPES.dropdown
         || selectedAttribute.dataType === ALL_DATA_TYPES.multi_select)) {
      fetchAttributeOptions({
        document: getActivityAttributeOptions,
        variables: {
          id: activityUpdateConfig.activityAttributeId,
          searchString: debouncedSearchText,
          limit: 50,
        },
      });
    }
  }, [
    activityUpdateConfig.activityAttributeId,
    activityUpdateConfig.activityId,
    activityAttributesData?.activityAttribute,
    fetchAttributeOptions,
    debouncedSearchText,
  ]);

  const transformedActivitiesData = transformCustomActivities(activitiesData);

  const getFieldOptions = useCallback(() => {
    const selectedAttribute = activityAttributesData?.activityAttribute?.find(
      (attr: ActivityAttributeType) => parseInt(attr.id, 10) === activityUpdateConfig.activityAttributeId,
    );

    if (!selectedAttribute || parseInt(selectedAttribute.activityId, 10) !== activityUpdateConfig.activityId) {
      return [];
    }

    const anyOption = { value: 'any', label: 'Any' };

    if (selectedAttribute.dataType === ALL_DATA_TYPES.dropdown
        || selectedAttribute.dataType === ALL_DATA_TYPES.multi_select) {
      const fieldOptions = attributeOptionsData?.activityAttributeOption?.map(
        (option: ActivityAttributeOption) => ({
          value: option.id,
          label: option.optionName,
        }),
      ) || [];

      return [anyOption, ...fieldOptions];
    }

    return [anyOption];
  }, [
    activityAttributesData?.activityAttribute,
    activityUpdateConfig.activityAttributeId,
    activityUpdateConfig.activityId,
    attributeOptionsData?.activityAttributeOption,
  ]);

  const fieldOptions = getFieldOptions();

  const handleOwnerChange = useCallback((value: string[]) => {
    if (value.includes('any')) {
      onConfigChange({
        ...activityUpdateConfig,
        toValue: 'any',
        fromValue: undefined,
      });
    } else {
      onConfigChange({
        ...activityUpdateConfig,
        toValue: value,
        fromValue: undefined,
      });
    }
  }, [activityUpdateConfig, onConfigChange]);

  const renderAttributeSpecificFields = () => {
    if (!activityUpdateConfig.activityAttributeId) return null;

    const selectedAttribute = activityAttributesData?.activityAttribute?.find(
      (attr: ActivityAttributeType) => parseInt(attr.id, 10) === activityUpdateConfig.activityAttributeId,
    );

    if (!selectedAttribute || parseInt(selectedAttribute.activityId, 10) !== activityUpdateConfig.activityId) {
      return null;
    }

    if (selectedAttribute.dataType === ALL_DATA_TYPES.reference_user) {
      return (
        <Row className={styles.changedAttributes}>
          <Col span={6}>
            <Typography.Text strong>
              Select Owner
            </Typography.Text>
          </Col>
          <Col span={12}>
            <OwnerSelectDropdown
              multiple
              selectProps={{ className: styles.fromToDropdown }}
              handleClick={handleOwnerChange as any}
              withAnyOption
              className={styles.fromToDropdown}
              value={activityUpdateConfig.toValue}
            />
          </Col>
        </Row>
      );
    }

    if (selectedAttribute.dataType === ALL_DATA_TYPES.dropdown
        || selectedAttribute.dataType === ALL_DATA_TYPES.multi_select) {
      return (
        <Row className={styles.changedAttributes}>
          <Col span={6}>
            <Typography.Text strong>
              Changes
            </Typography.Text>
          </Col>
          <Col span={12}>
            <Flex gap={8} vertical className={styles.attributeSpecificFields}>
              <Flex gap={8}>
                <Typography.Text className={styles.changedAttributesTitle}>
                  From
                </Typography.Text>
                <Select
                  value={activityUpdateConfig.fromValue}
                  options={fieldOptions}
                  onChange={(value) => {
                    onConfigChange({
                      ...activityUpdateConfig,
                      fromValue: value,
                    });
                  }}
                  placeholder="Select From Value"
                  showSearch
                  optionFilterProp="label"
                  onSearch={setSearchText}
                  filterOption={false}
                  notFoundContent={null}
                  className={styles.fromToDropdown}
                />
              </Flex>
              <Flex gap={8}>
                <Typography.Text className={styles.changedAttributesTitle}>
                  To
                </Typography.Text>
                <Select
                  value={activityUpdateConfig.toValue}
                  options={fieldOptions}
                  onChange={(value) => {
                    onConfigChange({
                      ...activityUpdateConfig,
                      toValue: value,
                    });
                  }}
                  placeholder="Select To Value"
                  showSearch
                  optionFilterProp="label"
                  onSearch={setSearchText}
                  filterOption={false}
                  notFoundContent={null}
                  className={styles.fromToDropdown}
                />
              </Flex>
            </Flex>
          </Col>
        </Row>
      );
    }

    return null;
  };

  return (
    <Flex gap={8} vertical>
      <Row className={styles.changedAttributes}>
        <Col span={6}>
          <Typography.Text strong>
            Select Activity
          </Typography.Text>
        </Col>
        <Col span={12}>
          <Select
            value={activityUpdateConfig.activityId ? activityUpdateConfig.activityId.toString() : undefined}
            options={transformedActivitiesData}
            onChange={(value) => {
              onConfigChange({
                ...activityUpdateConfig,
                activityId: parseInt(value, 10),
                activityAttributeId: null,
                fromValue: undefined,
                toValue: undefined,
              });
            }}
            loading={activitiesLoading}
            placeholder="Select Activity"
            className={styles.activitySelect}
            showSearch
            optionFilterProp="label"
          />
        </Col>
      </Row>

      {activityUpdateConfig.activityId && (
        <Row className={styles.changedAttributes}>
          <Col span={6}>
            <Typography.Text strong>
              Select Activity Field
            </Typography.Text>
          </Col>
          <Col span={12}>
            <Select
              value={
                !activityUpdateConfig.activityAttributeId
                  ? undefined
                  : activityUpdateConfig.activityAttributeId.toString()
              }
              options={activityAttributesData?.activityAttribute?.map((attr) => ({
                value: attr.id,
                label: attr.attributeName,
              }))}
              onChange={(value) => {
                const selectedAttribute = activityAttributesData?.activityAttribute?.find(
                  (attr) => attr.id === value,
                );

                const config: ActivityUpdateCriteria = {
                  ...activityUpdateConfig,
                  activityAttributeId: parseInt(value, 10),
                  fromValue: undefined,
                  toValue: undefined,
                };

                if (selectedAttribute?.dataType === ALL_DATA_TYPES.dropdown
                    || selectedAttribute?.dataType === ALL_DATA_TYPES.multi_select) {
                  config.fromValue = 'any';
                  config.toValue = 'any';
                } else if (selectedAttribute?.dataType === ALL_DATA_TYPES.reference_user) {
                  config.toValue = 'any';
                }

                onConfigChange(config);
              }}
              loading={attributesLoading}
              placeholder="Select Field"
              showSearch
              optionFilterProp="label"
              className={styles.activityAttributeDropdown}
            />
          </Col>
        </Row>
      )}

      {renderAttributeSpecificFields()}
    </Flex>
  );
}
